/**
 * recorder.js — shooting the takes inside the tool, with the first one showing
 * through.
 *
 * WHY THIS EXISTS. Every measurement in this tool tries to repair something
 * that went wrong BEFORE the user reached us: the tripod was bumped, or both
 * performers ended up standing in the same half of the frame. align.js can
 * measure the first and analyseSeam can only report the second — neither can
 * undo them. Both disappear if the second take is shot while the first one is
 * visible on screen and the camera is never touched in between.
 *
 * So the ghost is not a nicety, it IS the feature: a translucent playback of
 * take 1 over the live camera, running from the moment recording starts. The
 * performer sees where their other self is standing and when it stops talking.
 *
 * ⚠️ Ghost AUDIO is off by default and says why. Played through speakers it is
 * re-recorded by the microphone, and the finished composite then carries take
 * 1's voice twice — a defect that is invisible until export. With headphones it
 * is the single most useful control here, so it is offered, not hidden.
 *
 * Built on the project's own primitives, not on a private copy: CameraManager
 * (permission classification + localized errors in 7 locales + telemetry) and
 * RecordingGuard (chunks streamed to IndexedDB, so a crash or an accidental
 * navigation mid-take does not lose it).
 *
 * Exposes window.CloneRecorder.
 */
(function () {
'use strict';
if (window.CloneRecorder) return;

function ae(f, data) {
    try {
        var p = data || {};
        p.slug = 'clone-effect'; p.f = f;
        window.__ae && window.__ae('feature_use', p);
    } catch (_) {}
}

function available() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia
        && window.MediaRecorder && window.CameraManager);
}

/* The container the browser will actually give us. Preference order matters:
   mp4 lets the export take the fast decoder-pull path, webm only the seek one. */
function pickMime() {
    var want = [
        'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm'
    ];
    for (var i = 0; i < want.length; i++) {
        if (MediaRecorder.isTypeSupported(want[i])) return want[i];
    }
    return '';
}

function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
}

/**
 * @param opts {
 *   t:          (key) => string
 *   ghostClip:  a CloneScene clip to show through, or null for the first take
 *   takeNumber: 1-based, for the file name and the heading
 *   width/height: preferred capture size (matched to take 1 when known)
 *   onFile:     (File) => void
 * }
 */
function open(opts) {
    var t = opts.t || function (k) { return k; };
    var cam = new window.CameraManager({ slug: 'clone-effect' });
    var guard = window.RecordingGuard ? new window.RecordingGuard({ slug: 'clone-effect' }) : null;

    var stream = null, rec = null, chunks = [], mime = pickMime();
    var startedAt = 0, tick = 0, countdownTimer = 0, cancelled = false;
    var facing = 'user';
    var closed = false;

    /* ── markup ── */
    var dlg = el('dialog', 'ce-rec');
    dlg.setAttribute('aria-label', t('rec_title'));

    var head = el('div', 'ce-rec-head');
    head.appendChild(el('strong', null, t('rec_title').replace(':n', opts.takeNumber || 1)));
    var closeBtn = el('button', 'ce-rec-x', '✕');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', t('rec_close'));
    head.appendChild(closeBtn);
    dlg.appendChild(head);

    var stage = el('div', 'ce-rec-stage');
    var live = el('video', 'ce-rec-live');
    live.muted = true; live.playsInline = true; live.autoplay = true;
    stage.appendChild(live);

    var ghost = null, ghostWrap = null;
    if (opts.ghostClip) {
        ghost = el('video', 'ce-rec-ghost');
        ghost.src = opts.ghostClip.url;
        ghost.muted = true; ghost.playsInline = true; ghost.loop = false;
        ghost.style.opacity = '0.45';
        stage.appendChild(ghost);
    }

    /* Acquiring a camera takes seconds — measured 4–6 s here, and longer on a
       phone that has to wake the sensor. A black rectangle with a disabled
       button for that long reads as "broken", so the wait is named. */
    var waiting = el('div', 'ce-rec-wait');
    waiting.appendChild(el('span', 'ce-spinner'));
    waiting.appendChild(el('span', null, t('rec_starting')));
    stage.appendChild(waiting);

    var countdown = el('div', 'ce-rec-count');
    countdown.hidden = true;
    stage.appendChild(countdown);

    var dot = el('div', 'ce-rec-dot');
    dot.hidden = true;
    dot.appendChild(el('span', 'ce-rec-dot-led'));
    var elapsed = el('span', 'ce-rec-elapsed', '0:00');
    dot.appendChild(elapsed);
    stage.appendChild(dot);

    dlg.appendChild(stage);

    var err = el('div', 'ce-rec-error');
    err.hidden = true;
    dlg.appendChild(err);

    var ctrls = el('div', 'ce-rec-ctrls');

    /* Ghost controls only exist when there IS a first take. */
    if (ghost) {
        var opRow = el('label', 'ce-rec-row');
        opRow.appendChild(el('span', null, t('rec_ghost')));
        var op = el('input', 'ce-slider');
        op.type = 'range'; op.min = 10; op.max = 90; op.value = 45;
        op.addEventListener('input', function () { ghost.style.opacity = String(op.value / 100); });
        opRow.appendChild(op);
        ctrls.appendChild(opRow);

        var sndRow = el('label', 'ce-rec-row');
        var snd = el('input');
        snd.type = 'checkbox';
        snd.addEventListener('change', function () {
            ghost.muted = !snd.checked;
            ae('record_ghost_audio', { on: snd.checked ? 1 : 0 });
        });
        sndRow.appendChild(snd);
        sndRow.appendChild(el('span', null, t('rec_ghost_sound')));
        ctrls.appendChild(sndRow);
        ctrls.appendChild(el('div', 'ce-hint', t('rec_ghost_sound_hint')));
    }

    var btnRow = el('div', 'ce-rec-buttons');
    var flip = el('button', 'ce-btn ghost', '🔄 ' + t('rec_flip'));
    flip.type = 'button';
    var go = el('button', 'ce-btn primary big', '● ' + t('rec_start'));
    go.type = 'button';
    btnRow.appendChild(flip);
    btnRow.appendChild(go);
    ctrls.appendChild(btnRow);

    /* The one sentence that prevents the defect this whole tool works around. */
    var keep = el('div', 'ce-rec-keep', opts.ghostClip ? t('rec_keep_still_2') : t('rec_keep_still_1'));
    ctrls.appendChild(keep);

    dlg.appendChild(ctrls);
    document.body.appendChild(dlg);

    /* ── camera ── */
    async function startCamera() {
        waiting.hidden = false;
        try {
            stream = await cam.start({
                facingMode: facing,
                audio: true,
                width: opts.width || 1280,
                height: opts.height || 720
            });
            live.srcObject = stream;
            // Do not trust autoplay alone: a muted autoplay element inside a
            // freshly-shown <dialog> is not guaranteed to start, and the whole
            // dialog is useless without a picture.
            try { var pr = live.play(); if (pr && pr.catch) pr.catch(function () {}); } catch (_) {}
            // The wait ends when a FRAME exists, not when the promise resolves:
            // the stream is live several hundred ms before the element paints.
            var clearWait = function () { waiting.hidden = true; };
            if (live.requestVideoFrameCallback) {
                try { live.requestVideoFrameCallback(clearWait); } catch (_) { clearWait(); }
            } else {
                live.addEventListener('loadeddata', clearWait, { once: true });
            }
            setTimeout(clearWait, 8000);
            // A selfie preview that is not mirrored feels broken to everyone, but
            // the RECORDING must stay true — so the mirror is CSS on the stage,
            // and the ghost rides inside it so both flip together. Mirroring only
            // the camera would put the other performer on the wrong side.
            stage.classList.toggle('mirrored', facing === 'user');
            err.hidden = true;
            go.disabled = false;
            ae('record_camera_ok', { facing: facing });
            // The first video recorder of a Chromium tab drops its first ~2 s of
            // picture, and a camera recording needs the warm-up on its own track.
            // Warmed now: the take starts in step with the ghost, so it never waits.
            if (window.__mediaRecorderWarm) window.__mediaRecorderWarm({ stream: stream, mimeType: mime });
        } catch (e) {
            var msg = (e && e.code && cam.getErrorMessage) ? cam.getErrorMessage(e.code) : (e && e.message) || '';
            waiting.hidden = true;
            err.textContent = msg || t('rec_camera_failed');
            err.hidden = false;
            go.disabled = true;
            ae('record_camera_failed', { code: (e && e.code) || '' });
        }
    }

    flip.addEventListener('click', async function () {
        if (rec) return;
        facing = facing === 'user' ? 'environment' : 'user';
        await startCamera();
    });

    /* ── record ── */
    function fmt(s) {
        var m = Math.floor(s / 60);
        return m + ':' + String(Math.floor(s % 60)).padStart(2, '0');
    }

    function beginRecording() {
        if (!stream) return;
        chunks = [];
        try {
            rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
        } catch (e) {
            err.textContent = t('rec_no_recorder');
            err.hidden = false;
            ae('record_start_failed', { err: String(e && e.message).slice(0, 60) });
            return;
        }
        rec.ondataavailable = function (e) {
            if (!e.data || !e.data.size) return;
            chunks.push(e.data);
            guard && guard.pushChunk(e.data);
        };
        rec.onstop = finishRecording;

        if (guard) {
            guard.begin({ mimeType: rec.mimeType, label: 'take ' + (opts.takeNumber || 1) });
            guard.attachRecorder(rec);
        }
        rec.start(guard ? guard.timesliceMs : 1000);

        startedAt = Date.now();
        dot.hidden = false;
        go.textContent = '■ ' + t('rec_stop');
        flip.disabled = true;
        tick = setInterval(function () {
            elapsed.textContent = fmt((Date.now() - startedAt) / 1000);
        }, 250);

        if (ghost) {
            // From zero, every time: the ghost is a partner to answer, and a
            // partner that starts in the middle of its sentence is useless.
            try { ghost.currentTime = 0; ghost.play(); } catch (_) {}
        }
        ae('record_start', { take: opts.takeNumber || 1, ghost: ghost ? 1 : 0, mime: (rec.mimeType || '').slice(0, 40) });
    }

    function finishRecording() {
        clearInterval(tick);
        dot.hidden = true;
        // ✕ means abandon. Without this flag MediaRecorder's onstop still fired
        // and handed over the partial take — the user cancelled and got a file
        // anyway. Re-recording is cheap; a take you did not ask for is confusing.
        if (cancelled) {
            chunks = [];
            rec = null;
            ae('record_cancelled', { take: opts.takeNumber || 1, secs: Math.round((Date.now() - startedAt) / 1000) });
            try { window.__toast && window.__toast(t('rec_discarded'), 'info'); } catch (_) {}
            return;
        }
        var secs = (Date.now() - startedAt) / 1000;
        var type = (rec && rec.mimeType) || mime || 'video/webm';
        var blob = new Blob(chunks, { type: type.split(';')[0] });
        rec = null;
        guard && guard.finish();
        if (ghost) { try { ghost.pause(); } catch (_) {} }

        ae('record_done', { take: opts.takeNumber || 1, secs: Math.round(secs), bytes: blob.size });

        if (blob.size < 1024) {
            err.textContent = t('rec_too_short');
            err.hidden = false;
            go.textContent = '● ' + t('rec_start');
            flip.disabled = false;
            return;
        }
        var ext = /mp4/.test(type) ? 'mp4' : 'webm';
        var file = new File([blob], 'take-' + (opts.takeNumber || 1) + '.' + ext, { type: blob.type });
        close();
        opts.onFile && opts.onFile(file);
    }

    function countTo(n, then) {
        countdown.hidden = false;
        countdown.textContent = String(n);
        countdownTimer = setInterval(function () {
            n--;
            if (n <= 0) {
                clearInterval(countdownTimer); countdownTimer = 0;
                countdown.hidden = true;
                then();
                return;
            }
            countdown.textContent = String(n);
        }, 1000);
    }

    go.addEventListener('click', function () {
        if (rec) { try { rec.stop(); } catch (_) {} return; }
        // A second press during the countdown ABORTS it. Doing nothing there is
        // a three-second dead end: the user has changed their mind, the button
        // is right under their finger, and the only way out was the ✕.
        if (countdownTimer) {
            clearInterval(countdownTimer); countdownTimer = 0;
            countdown.hidden = true;
            go.textContent = '● ' + t('rec_start');
            ae('record_countdown_aborted', { take: opts.takeNumber || 1 });
            return;
        }
        go.textContent = t('rec_cancel_countdown');
        countTo(3, function () {
            go.textContent = '■ ' + t('rec_stop');
            beginRecording();
        });
    });

    /* ── close ── */
    function close() {
        if (closed) return;
        closed = true;
        clearInterval(tick); clearInterval(countdownTimer);
        if (rec) {
            cancelled = true;
            try { rec.stop(); } catch (_) {}
            guard && guard.discardActive();
        }
        try { cam.destroy(); } catch (_) {}
        try { live.srcObject = null; } catch (_) {}
        if (ghost) { try { ghost.pause(); ghost.removeAttribute('src'); } catch (_) {} }
        try { dlg.close(); } catch (_) {}
        try { dlg.remove(); } catch (_) {}
    }
    closeBtn.addEventListener('click', close);
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });

    try { dlg.showModal(); } catch (_) { dlg.setAttribute('open', ''); }
    go.disabled = true;
    startCamera();
    ae('record_open', { take: opts.takeNumber || 1, ghost: opts.ghostClip ? 1 : 0 });

    return { close: close };
}

window.CloneRecorder = { available: available, open: open, pickMime: pickMime };
})();
